import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { rutaInicio } from './utils/permisos';
import ProtectedRoute from './components/ProtectedRoute';
import { SesionVencidaDialog } from './components/SesionVencidaDialog';
import { Toaster } from 'sonner';
import './App.css';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CamaraHost } from './components/camara/CamaraEnVivo';
import { lazyResiliente, precargarPantallas } from './utils/lazyResiliente';

// Lazy load pages
const Login = lazyResiliente(() => import('./pages/Login'), 'Login');
const Dashboard = lazyResiliente(() => import('./pages/Dashboard'), 'Dashboard');
const SinSecciones = lazyResiliente(() => import('./pages/SinSecciones'), 'SinSecciones');
const Clientes = lazyResiliente(() => import('./pages/Clientes'), 'Clientes');
const Locales = lazyResiliente(() => import('./pages/Locales'), 'Locales');
const Usuarios = lazyResiliente(() => import('./pages/Usuarios'), 'Usuarios');
const PlanillaTecnica = lazyResiliente(() => import('./pages/PlanillaTecnica'), 'PlanillaTecnica');
const Historial = lazyResiliente(() => import('./pages/Historial'), 'Historial');
const Stock = lazyResiliente(() => import('./pages/Stock'), 'Stock');
const ServicioDetalle = lazyResiliente(() => import('./pages/ServicioDetalle'), 'ServicioDetalle');
const Aprobaciones = lazyResiliente(() => import('./pages/Aprobaciones'), 'Aprobaciones');
const Tendencias = lazyResiliente(() => import('./pages/Tendencias2'), 'Tendencias');
const Auditorias = lazyResiliente(() => import('./pages/Auditorias'), 'Auditorias');
const AuditoriaDetalle = lazyResiliente(() => import('./pages/AuditoriaDetalle'), 'AuditoriaDetalle');
const ProgramacionServicios = lazyResiliente(() => import('./pages/ProgramacionServicios'), 'ProgramacionServicios');
const MisServicios = lazyResiliente(() => import('./pages/MisServicios'), 'MisServicios');
const MiAsistencia = lazyResiliente(() => import('./pages/MiAsistencia'), 'MiAsistencia');
const RelevamientoNoProgramado = lazyResiliente(() => import('./pages/RelevamientoNoProgramado'), 'RelevamientoNoProgramado');
const Papelera = lazyResiliente(() => import('./pages/Papelera'), 'Papelera');
const Rendimiento = lazyResiliente(() => import('./pages/Rendimiento'), 'Rendimiento');
const AuditoriaServicioDetalle = lazyResiliente(() => import('./pages/AuditoriaServicioDetalle'), 'AuditoriaServicioDetalle');
const AuditoriaServicio = lazyResiliente(() => import('./pages/AuditoriaServicio'), 'AuditoriaServicio');

// Cliente Portal
const ClientePortalLogin = lazyResiliente(() => import('./pages/portal/ClientePortalLogin'), 'ClientePortalLogin');
const ClientePortalRestablecer = lazyResiliente(() => import('./pages/portal/ClientePortalRestablecer'), 'ClientePortalRestablecer');
const RestablecerPassword = lazyResiliente(() => import('./pages/RestablecerPassword'), 'RestablecerPassword');
const ClientePortalDashboard = lazyResiliente(() => import('./pages/portal/ClientePortalDashboard'), 'ClientePortalDashboard');
const ClientePortalServicios = lazyResiliente(() => import('./pages/portal/ClientePortalServicios'), 'ClientePortalServicios');
const ClientePortalServicioDetalle = lazyResiliente(() => import('./pages/portal/ClientePortalServicioDetalle'), 'ClientePortalServicioDetalle');
const ClientePortalTendencia = lazyResiliente(() => import('./pages/portal/ClientePortalTendencia'), 'ClientePortalTendencia');
const ClientePortalAuditorias = lazyResiliente(() => import('./pages/portal/ClientePortalAuditorias'), 'ClientePortalAuditorias');
const ClientePortalDocumentaciones = lazyResiliente(() => import('./pages/portal/ClientePortalDocumentaciones'), 'ClientePortalDocumentaciones');
const ClientePortalPlanos = lazyResiliente(() => import('./pages/portal/ClientePortalPlanos'), 'ClientePortalPlanos');
const PlanosInteractivos = lazyResiliente(() => import('./pages/PlanosInteractivos'), 'PlanosInteractivos');
const MapaPage = lazyResiliente(() => import('./pages/Mapa'), 'Mapa');
const GestionInterna = lazyResiliente(() => import('./pages/GestionInterna'), 'GestionInterna');
const VisorFoto = lazyResiliente(() => import('./pages/VisorFoto'), 'VisorFoto');

const Loading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Cargando...</p>
    </div>
  </div>
);

// Redirige técnicos al login a Planilla Técnica en vez del Dashboard
const HomeRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={rutaInicio(user)} replace />;
};

function App() {
  // Pre-carga silenciosa de las pantallas del técnico: quedan en caché aunque después la red sea lenta o no haya señal
  useEffect(() => {
    precargarPantallas([
      () => import('./pages/Login'), () => import('./pages/MisServicios'), () => import('./pages/PlanillaTecnica'),
      () => import('./pages/ServicioDetalle'), () => import('./pages/MiAsistencia'), () => import('./pages/RelevamientoNoProgramado'),
    ]);
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* Personal Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/restablecer" element={<RestablecerPassword />} />
            <Route path="/foto" element={<VisorFoto />} />
            <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute modulo="dashboard"><Dashboard /></ProtectedRoute>} />
            <Route path="/sin-secciones" element={<ProtectedRoute><SinSecciones /></ProtectedRoute>} />
            
            {/* Solo Admin y Supervisor (no Técnicos) */}
            <Route path="/clientes" element={<ProtectedRoute modulo="clientes"><Clientes /></ProtectedRoute>} />
            <Route path="/locales" element={<ProtectedRoute modulo="clientes"><Locales /></ProtectedRoute>} />
            
            {/* Solo Admin */}
            <Route path="/usuarios" element={<ProtectedRoute modulo="configuracion"><Usuarios /></ProtectedRoute>} />
            {/* Backward-compat: /configuracion redirige a /usuarios */}
            <Route path="/configuracion" element={<Navigate to="/usuarios" replace />} />
            
            {/* Todos los roles */}
            <Route path="/planilla-tecnica" element={<ProtectedRoute modulo="planilla_tecnica"><PlanillaTecnica /></ProtectedRoute>} />
            <Route path="/historial" element={<ProtectedRoute modulo="historial"><Historial /></ProtectedRoute>} />
            <Route path="/servicio/:id" element={<ProtectedRoute><ServicioDetalle /></ProtectedRoute>} />

            {/* Programación de Servicios — Admin y Supervisor */}
            <Route path="/programacion" element={<ProtectedRoute modulo="programacion"><ProgramacionServicios /></ProtectedRoute>} />
            {/* Vista técnico */}
            <Route path="/mis-servicios" element={<ProtectedRoute modulo="mis_servicios"><MisServicios /></ProtectedRoute>} />
            <Route path="/relevamiento-no-programado" element={<ProtectedRoute modulo="planilla_tecnica"><RelevamientoNoProgramado /></ProtectedRoute>} />
            <Route path="/mi-asistencia" element={<ProtectedRoute modulo="mi_asistencia"><MiAsistencia /></ProtectedRoute>} />
            
            {/* Solo Admin y Supervisor */}
            <Route path="/aprobaciones" element={<ProtectedRoute modulo="aprobaciones"><Aprobaciones /></ProtectedRoute>} />
            <Route path="/tendencias" element={<ProtectedRoute modulo="tendencias"><Tendencias /></ProtectedRoute>} />
            <Route path="/planos" element={<ProtectedRoute modulo="planos"><PlanosInteractivos /></ProtectedRoute>} />
            <Route path="/mapa" element={<ProtectedRoute modulo="mapa"><MapaPage /></ProtectedRoute>} />
            <Route path="/gestion-interna" element={<ProtectedRoute modulo="gestion_interna"><GestionInterna /></ProtectedRoute>} />
            <Route path="/auditorias/:id" element={<ProtectedRoute modulo="rendimiento"><AuditoriaDetalle /></ProtectedRoute>} />
            <Route path="/rendimiento" element={<ProtectedRoute modulo="rendimiento"><Rendimiento /></ProtectedRoute>} />
            <Route path="/rendimiento/auditoria-servicio/:id" element={<ProtectedRoute modulo="auditoria_servicio"><AuditoriaServicioDetalle /></ProtectedRoute>} />
            <Route path="/auditoria-servicio" element={<ProtectedRoute modulo="auditoria_servicio"><AuditoriaServicio /></ProtectedRoute>} />
            <Route path="/papelera" element={<ProtectedRoute modulo="papelera"><Papelera /></ProtectedRoute>} />
            <Route path="/stock" element={<ProtectedRoute modulo="stock"><Stock /></ProtectedRoute>} />
            
            {/* Cliente Portal */}
            <Route path="/portal/login" element={<ClientePortalLogin />} />
            <Route path="/portal/restablecer" element={<ClientePortalRestablecer />} />
            <Route path="/portal" element={<ProtectedRoute clienteOnly={true}><ClientePortalDashboard /></ProtectedRoute>} />
            <Route path="/portal/servicios" element={<ProtectedRoute clienteOnly={true}><ClientePortalServicios /></ProtectedRoute>} />
            <Route path="/portal/servicio/:id" element={<ProtectedRoute clienteOnly={true}><ServicioDetalle /></ProtectedRoute>} />
            <Route path="/portal/tendencia" element={<ProtectedRoute clienteOnly={true}><ClientePortalTendencia /></ProtectedRoute>} />
            <Route path="/portal/auditorias" element={<ProtectedRoute clienteOnly={true}><ClientePortalAuditorias /></ProtectedRoute>} />
            <Route path="/portal/auditoria/:id" element={<ProtectedRoute clienteOnly={true}><AuditoriaDetalle /></ProtectedRoute>} />
            <Route path="/portal/documentaciones" element={<ProtectedRoute clienteOnly={true}><ClientePortalDocumentaciones /></ProtectedRoute>} />
            <Route path="/portal/planos" element={<ProtectedRoute clienteOnly={true}><ClientePortalPlanos /></ProtectedRoute>} />
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
        <SesionVencidaDialog />
      </BrowserRouter>
      <Toaster position="top-right" />
      <CamaraHost />
    </AuthProvider>
  );
}

export default App;